import { useEffect } from 'react';
import { useFieldArray, useFormContext, type FieldErrors } from 'react-hook-form';
import { Input, Select } from '@/components/common';
import {
  ADULTS_OPTIONS,
  CHILDREN_OPTIONS,
  HEALTH_MEMBER_RELATIONSHIP_OPTIONS,
  calculateAge,
  type HealthQuoteFormData,
} from '../../schema';

function todayISO(): string {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/**
 * Step 2 of the Health branch: Adults (1-4) + Children (0-4) selectors, each
 * driving its OWN dynamic member sub-forms.
 *
 * Members are held in TWO role-keyed field arrays (`adult_members`,
 * `child_members`) rather than one positional array. Each array is grown/
 * shrunk by its own count, so a member's Relationship + Date of Birth always
 * stays attached to the adult/child slot it was entered under — changing the
 * OTHER count never re-labels or misattributes an existing member's data (the
 * bug a single tail-appended/tail-removed positional array had). The two
 * arrays are merged into the single `members` payload array only at submit
 * time (QuoteWizardPage.onHealthSubmit); the backend shape is unchanged.
 */
export function InsuredDetailsStep() {
  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext<HealthQuoteFormData>();

  const adultArray = useFieldArray({ control, name: 'adult_members' });
  const childArray = useFieldArray({ control, name: 'child_members' });
  const adultsCount = Number(watch('adults')) || 0;
  const childrenCount = Number(watch('children')) || 0;

  // Keep each array's length in lockstep with its OWN count. Append/remove is
  // per-array, so growing/shrinking adults never touches child slots (and vice
  // versa) — data stays pinned to its role.
  useEffect(() => {
    if (adultsCount > adultArray.fields.length) {
      for (let i = adultArray.fields.length; i < adultsCount; i++) {
        adultArray.append({ relation: '', date_of_birth: '' }, { shouldFocus: false });
      }
    } else if (adultsCount < adultArray.fields.length) {
      for (let i = adultArray.fields.length - 1; i >= adultsCount; i--) {
        adultArray.remove(i);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adultsCount]);

  useEffect(() => {
    if (childrenCount > childArray.fields.length) {
      for (let i = childArray.fields.length; i < childrenCount; i++) {
        childArray.append({ relation: '', date_of_birth: '' }, { shouldFocus: false });
      }
    } else if (childrenCount < childArray.fields.length) {
      for (let i = childArray.fields.length - 1; i >= childrenCount; i--) {
        childArray.remove(i);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childrenCount]);

  const renderMemberForms = (
    kind: 'adult' | 'child',
    fields: { id: string }[],
    fieldErrors: FieldErrors<HealthQuoteFormData>['adult_members']
  ) => {
    const arrayName = kind === 'adult' ? 'adult_members' : 'child_members';
    const heading = kind === 'adult' ? 'Adult' : 'Child';
    return fields.map((field, index) => {
      const dob = watch(`${arrayName}.${index}.date_of_birth`);
      const age = calculateAge(dob);
      const memberErrors = fieldErrors?.[index];

      return (
        <div key={field.id} className="border-t border-gray-100 dark:border-gray-700 pt-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
            {heading} {index + 1}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Select
              label="Relationship"
              options={HEALTH_MEMBER_RELATIONSHIP_OPTIONS}
              placeholder="Select relationship"
              required
              error={memberErrors?.relation?.message}
              {...register(`${arrayName}.${index}.relation`)}
            />
            <Input
              label="Date of Birth"
              type="date"
              max={todayISO()}
              required
              error={memberErrors?.date_of_birth?.message}
              {...register(`${arrayName}.${index}.date_of_birth`)}
            />
            <Input
              label="Age"
              value={age !== undefined ? `${age} years` : ''}
              readOnly
              disabled
              helperText="Auto-calculated from date of birth"
            />
          </div>
        </div>
      );
    });
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Insured Details</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
          Tell us who needs to be covered under this policy.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Select
            label="Adults"
            options={ADULTS_OPTIONS}
            placeholder="Select number of adults"
            required
            error={errors.adults?.message}
            {...register('adults')}
          />
          <Select
            label="Children"
            options={CHILDREN_OPTIONS}
            placeholder="Select number of children"
            required
            error={errors.children?.message}
            {...register('children')}
          />
        </div>
      </div>

      <div className="space-y-6">
        {renderMemberForms('adult', adultArray.fields, errors.adult_members)}
        {renderMemberForms('child', childArray.fields, errors.child_members)}
      </div>
    </div>
  );
}
